import { FactionCache } from "./faction.cache";
import { AspectCache } from "./aspect.cache";
import { PrototypeCache } from "./prototype.cache";
import { TypeCache } from "./type.cache";
import { Aspect, Branch, Faction, Node, Prototype } from "@xanadu/model";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { RoleCache } from "./role.cache";
import { UnitCache } from "./unit.cache";
import { AuthSignal } from "@xanadu/signal";
import { EndpointCache } from "./endpoint.cache";
import { RealmCache } from "./realm.cache";
import { CategoryCache } from "./category.cache";
import { ClientCache } from "./client.cache";
import { ClubCache } from "./club.cache";

@Injectable({
    providedIn: 'root'
})
export class CacheAggregator {
    private readonly authSignal = inject(AuthSignal);
    private readonly aspectCache = inject(AspectCache);
    private readonly prototypeCache = inject(PrototypeCache);
    private readonly factionCache = inject(FactionCache);
    private readonly typeCache = inject(TypeCache);
    private readonly categoryCache = inject(CategoryCache);
    private readonly roleCache = inject(RoleCache);
    private readonly unitCache = inject(UnitCache);
    private readonly clientCache = inject(ClientCache);
    private readonly clubCache = inject(ClubCache);
    private readonly endpointCache = inject(EndpointCache);
    private readonly realmCache = inject(RealmCache);

    private readonly aspects = computed(() => this.aspectCache.signal());
    private readonly prototypes = computed(() => this.prototypeCache.signal());
    private readonly factions = computed(() => this.factionCache.signal());
    private readonly types = computed(() => this.typeCache.signal());
    private readonly categories = computed(() => this.categoryCache.signal());
    private readonly roles = computed(() => this.roleCache.signal());
    private readonly units = computed(() => this.unitCache.signal());
    private readonly clients = computed(() => this.clientCache.signal());
    private readonly clubs = computed(() => this.clubCache.signal());
    private readonly endpoints = computed(() => this.endpointCache.signal());
    private readonly realms = computed(() => this.realmCache.signal());

    private readonly readyAspect = signal<boolean>(false);
    private readonly readyType = signal<boolean>(false);
    private readonly readyRole = signal<boolean>(false);
    private readonly readyClient = signal<boolean>(false);
    private readonly readyEndpoint = signal<boolean>(false);

    readonly ready = computed((): boolean => {
        return this.readyAspect() && this.readyType() && this.readyRole() && this.readyClient() && this.readyEndpoint();
    });

    constructor(
    ) {
        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                if (identity._system || identity.permit?.schema || identity.permit?.content || identity.permit?.dataType) {
                    const aspects = this.aspects();
                    const prototypes = this.prototypes();
                    const factions = this.factions();

                    if (aspects && prototypes && factions) {
                        this.initAspect(factions, aspects, prototypes);
                        this.readyAspect.set(true);
                    }
                } else {
                    this.readyAspect.set(true);
                }
            }
        });

        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                if (identity._system || identity.permit?.dataType || identity.permit?.schema || identity.permit?.content) {
                    const types = this.types();
                    const categories = this.categories();

                    if (types && categories) {
                        this.initParent(categories, types);
                        this.readyType.set(true);
                    }
                } else {
                    this.readyType.set(true);
                }
            }
        });

        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                if (identity._system || identity.permit?.security) {
                    const roles = this.roles();
                    const units = this.units();

                    if (roles && units) {
                        this.initParent(units, roles);
                        this.readyRole.set(true);
                    }
                } else {
                    this.readyRole.set(true);
                }
            }
        });

        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                if (identity._system || identity.permit?.security) {
                    const clients = this.clients();
                    const clubs = this.clubs();

                    if (clients && clubs) {
                        this.initParent(clubs, clients);
                        this.readyClient.set(true);
                    }
                } else {
                    this.readyClient.set(true);
                }
            }
        });

        effect(() => {
            const identity = this.authSignal.identity();

            if (identity) {
                if (identity._system || identity.permit?.import || identity.permit?.content) {
                    const endpoints = this.endpoints();
                    const realms = this.realms();

                    if (endpoints && realms) {
                        this.initParent(realms, endpoints);
                        this.readyEndpoint.set(true);
                    }
                } else {
                    this.readyEndpoint.set(true);
                }
            }
        });
    }

    private initAspect(factions: Map<string, Faction>, aspects: Map<string, Aspect>, prototypes: Map<string, Prototype>): void {
        aspects.forEach(aspect => {
            aspect.parent = aspect.parentId ? factions.get(aspect.parentId) : undefined;
            aspect.load();
        });

        prototypes.forEach(prototype => {
            prototype.parent = prototype.parentId ? factions.get(prototype.parentId) : undefined;
            prototype.load();
        });
    }

    private initParent<TP extends Branch, TC extends Node>(parents: Map<string, TP>, children: Map<string, TC>): void {
        children.forEach(child => {
            child.parent = child.parentId ? parents.get(child.parentId) : undefined;
        });
    }
}
